import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Tab,
  Tabs,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Quiz as QuizIcon,
  Group as GroupIcon,
  Assignment as AssignmentIcon,
  School as SchoolIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import {
  useGetAdminExamsQuery,
  useGetClassesQuery,
  useGetExamClassAssignmentsQuery,
  useAssignExamToClassesMutation,
  useRemoveExamClassAssignmentMutation,
  useGetExamAssignedClassesQuery,
  useLazyGetAdminExamsQuery,
} from '@/store/api/adminApi';
import useDebounce from '@/hooks/useDebounce';
import { CLASSES_PAGE_SIZE } from '@/constants/examConstants';
import ExamAssignmentDialog from '@/components/admin/ExamAssignmentDialog';
import ClassAssignedExamsDialog from '@/components/admin/ClassAssignedExamsDialog';
import ExamList from './ExamList';
import ExerciseList from './ExerciseList';
import ClassList from './ClassList';
import AssignmentList from './AssignmentList';

const TAB_INDEX = {
  EXAMS: 0,
  EXERCISES: 1,
  CLASSES: 2,
  ASSIGNMENTS: 3,
}

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`assignment-tabpanel-${index}`}
      aria-labelledby={`assignment-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const ExamAssignmentManagement = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedExam, setSelectedExam] = useState(null);
  const [assignmentDialogOpen, setAssignmentDialogOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classExamsDialogOpen, setClassExamsDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(9);
  const [classPage, setClassPage] = useState(1);
  const debouncedSearchTerm = useDebounce(searchTerm, 400);

  // API calls
  const [getExams, { data: examsData, isLoading: examsLoading, isFetching: examsFetching }] = useLazyGetAdminExamsQuery();
  const {
    data: classesData,
    isLoading: classesLoading,
    isFetching: classesFetching,
  } = useGetClassesQuery({
    page: classPage,
    limit: CLASSES_PAGE_SIZE,
    search: debouncedSearchTerm,
  });
  // const { data: assignmentsData, isLoading: assignmentsLoading } = useGetExamClassAssignmentsQuery();

  const exams = useMemo(() => examsData?.data || [], [examsData]);
  const classes = useMemo(() => classesData?.data?.classes || [], [classesData]);
  const classPagination = classesData?.data?.pagination;
  const totalClasses = classPagination?.total ?? classes.length;

  // Go back to the first page whenever the search changes
  useEffect(() => {
    setClassPage(1);
  }, [debouncedSearchTerm]);
  // const assignments = useMemo(() => assignmentsData?.data || [], [assignmentsData]);

  useEffect(() => {
    switch (activeTab) {
      case TAB_INDEX.EXAMS:
        getExams({
          page,
          limit: rowsPerPage,
          search: searchTerm,
        });
        break;
      case TAB_INDEX.EXERCISES:
        // Exercises are loaded via RTK Query
        break;
      case TAB_INDEX.CLASSES:
        // getClasses({
        //   page,
        //   limit: rowsPerPage,
        //   search: searchTerm,
        // });
        break;
      case TAB_INDEX.ASSIGNMENTS:
        // getAssignments({
        //   page,
        //   limit: rowsPerPage,
        //   search: searchTerm,
        // });
        break;
      default:
        break;
    }
  }, [page, rowsPerPage, searchTerm]);


  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
  }, []);

  const handleOpenAssignmentDialog = useCallback((exam) => {
    setSelectedExam(exam);
    setAssignmentDialogOpen(true);
  }, []);

  const handleCloseAssignmentDialog = useCallback(() => {
    setSelectedExam(null);
    setAssignmentDialogOpen(false);
  }, []);

  const handleViewAssignedExams = useCallback((classData) => {
    setSelectedClass(classData);
    setClassExamsDialogOpen(true);
  }, []);

  const handleCloseClassExamsDialog = useCallback(() => {
    setSelectedClass(null);
    setClassExamsDialogOpen(false);
  }, []);

  // const filteredExams = exams.filter(exam =>
  //   exam.title?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
  //   exam.description?.toLowerCase().includes(searchTerm?.toLowerCase())
  // );

  // Get assignment statistics©
  // const getExamAssignmentCount = (examId) => {
  //   return assignments.filter(assignment => assignment.examId === examId).length;
  // };

  // const getClassAssignmentCount = (classId) => {
  //   return assignments.filter(assignment => assignment.classId === classId).length;
  // };
  

  return (
    <Box sx={{ p: 3, minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AssignmentIcon sx={{ fontSize: 40 }} />
            <Box>
              <Typography variant="h4" fontWeight="bold">
                Assignments Management
              </Typography>
              <Typography variant="subtitle1" sx={{ opacity: 0.9, mt: 1 }}>
                Manage exam and exercise assignments for classes
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
              color: 'white',
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <QuizIcon sx={{ fontSize: 30 }} />
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {exams.length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Exams
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} sm={6} md={4}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
              color: 'white',
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SchoolIcon sx={{ fontSize: 30 }} />
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {totalClasses}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Classes
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #FF9800 0%, #F57C00 100%)',
              color: 'white',
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AssignmentIcon sx={{ fontSize: 30 }} />
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {assignments.length}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Total Assignments
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid> */}

        {/* <Grid item xs={12} sm={6} md={4}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #9C27B0 0%, #7B1FA2 100%)',
              color: 'white',
              borderRadius: 2,
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <GroupIcon sx={{ fontSize: 30 }} />
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {Math.round(assignments.length / Math.max(exams.length, 1) * 10) / 10}
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Avg. per Exam
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid> */}
      </Grid>

      {/* Main Content */}
      <Card sx={{ borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.1)' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                py: 2,
              },
              '& .Mui-selected': {
                color: '#2196F3',
              },
            }}
          >
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <QuizIcon />
                  Exams ({exams.length})
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssignmentIcon />
                  Exercises
                </Box>
              }
            />
            <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SchoolIcon />
                  Classes ({totalClasses})
                </Box>
              }
            />
            {/* <Tab
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AssignmentIcon />
                  All Assignments ({assignments.length})
                </Box>
              }
            /> */}
          </Tabs>
        </Box>

        {/* Search Bar */}
        <Box sx={{ p: 3, pb: 0 }}>
          <TextField
            fullWidth
            placeholder={`Search ${activeTab === 0 ? 'exams' : activeTab === 1 ? 'exercises' : activeTab === 2 ? 'classes' : 'assignments'}...`}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </Box>

        {/* Tab Panels */}
        <TabPanel value={activeTab} index={0}>
          {/* Exams Tab */}
          <ExamList
            examsLoading={examsLoading || examsFetching}
            // filteredExams={filteredExams}
            onPageChange={setPage}
            examsData={examsData}
            // getExamAssignmentCount={getExamAssignmentCount}
            handleOpenAssignmentDialog={handleOpenAssignmentDialog}
          />
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          {/* Exercises Tab */}
          <ExerciseList />
        </TabPanel>

        <TabPanel value={activeTab} index={2}>
          {/* Classes Tab */}
          <ClassList
            classesLoading={classesLoading || classesFetching}
            classes={classes}
            page={classPage}
            totalPages={classPagination?.pages || 1}
            onPageChange={setClassPage}
            // getClassAssignmentCount={getClassAssignmentCount}
            onViewAssignedExams={handleViewAssignedExams}
          />
        </TabPanel>

        {/* <TabPanel value={activeTab} index={2}>
          <AssignmentList
            assignments={assignments}
            assignmentsLoading={assignmentsLoading}
            page={page}
            rowsPerPage={rowsPerPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
          />
        </TabPanel> */}
      </Card>

      {/* Assignment Dialog */}
      <ExamAssignmentDialog
        open={assignmentDialogOpen}
        onClose={handleCloseAssignmentDialog}
        exam={selectedExam}
      />

      {/* Class Assigned Exams Dialog */}
      <ClassAssignedExamsDialog
        open={classExamsDialogOpen}
        onClose={handleCloseClassExamsDialog}
        classData={selectedClass}
      />
    </Box>
  );
};

export default ExamAssignmentManagement;
